// src/api/authHelpers.js

import { jwtDecode } from "jwt-decode";
import { seedAccountThemeFromLoginUser, seedActorAccountThemeFromInvite } from "./account-branding-theme";
import { clearStoredActorInviteAccountId } from "./account-context";

export const getUserType = () => {
    return localStorage.getItem("userType"); // 'actor' or 'user'
};

export const getToken = () => {
    const userType = getUserType();
    if (userType === "actor") {
        return localStorage.getItem("actorToken");
    }
    return localStorage.getItem("token");
};

export const clearUserSession = () => {
    const userType = getUserType();

    if (userType === "actor") {
        localStorage.removeItem("actorToken");
        localStorage.removeItem("actorData");
        localStorage.removeItem("actorMobileNo");
        clearStoredActorInviteAccountId();
    } else {
        localStorage.removeItem("token");
        localStorage.removeItem("userdata");
        // Stale accountId lets FallbackRoute bounce unauthenticated users to dashboard.
        localStorage.removeItem("accountId");
    }

    localStorage.removeItem("userType");
    try {
        const skipKeys = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (key && key.startsWith("tv:skip-theme-fetch:")) {
                skipKeys.push(key);
            }
        }
        skipKeys.forEach((key) => localStorage.removeItem(key));
    } catch {
        /* ignore */
    }
};

/** True when JWT exists, decodes, and is not expired (and not MFA-pending). */
export const isValidAccessToken = (token) => {
    if (!token || typeof token !== "string") {
        return false;
    }
    try {
        const decoded = jwtDecode(token);
        if (!decoded || typeof decoded !== "object") {
            return false;
        }
        if (decoded.mfaPending === true) {
            return false;
        }
        if (decoded.exp != null && Number(decoded.exp) * 1000 <= Date.now()) {
            return false;
        }
        return true;
    } catch {
        return false;
    }
};

/** Active platform (non-actor) session required for auth-protected app routes. */
export const hasActiveUserSession = () => {
    if (getUserType() !== "user") {
        return false;
    }
    return isValidAccessToken(getUserToken());
};

/** Store `userdata` and apply account theme when the API included `account.branding_theme`. */
export const persistUserData = (user) => {
    if (!user) {
        return;
    }
    localStorage.setItem("userdata", JSON.stringify(user));
    if (getUserType() !== "actor") {
        seedAccountThemeFromLoginUser(user);
    }
    if (typeof window !== "undefined" && user?.account) {
        window.dispatchEvent(
            new CustomEvent("tv:account-profile-updated", {
                detail: { account: user.account, updatedAt: Date.now() },
            })
        );
    }
};

export const getUserToken = () => localStorage.getItem("token");
export const getActorToken = () => localStorage.getItem("actorToken");

export const getUserData = () => {
    try {
        return JSON.parse(localStorage.getItem("userdata")) || null;
    } catch {
        return null;
    }
};

export const getActorData = () => {
    try {
        return JSON.parse(localStorage.getItem("actorData")) || null;
    } catch {
        return null;
    }
};

/** Store actor session payload and apply account branding when present. */
export const persistActorData = (payload) => {
    if (!payload) {
        return;
    }
    const sessionPayload = payload.user ? payload : { user: payload };
    localStorage.setItem("actorData", JSON.stringify(sessionPayload));
    if (sessionPayload.user?.account) {
        seedActorAccountThemeFromInvite(sessionPayload.user.account);
    }
};

/** Account id embedded in the actor onboarding JWT (used when routes are not account-scoped). */
export const getActorAccountIdFromToken = () => {
    try {
        const token = getActorToken();
        if (!token) {
            return null;
        }
        const decoded = jwtDecode(token);
        if (decoded?.account_id === null || decoded?.account_id === undefined) {
            return null;
        }
        return String(decoded.account_id);
    } catch {
        return null;
    }
};

